import { convertHM } from '../../../../../../utils/timeCalculation';
import { TASK_STATUS_COLORS, TASK_STATUS_LIST, TASK_TYPE_LIST } from '../../../../constants';
import DelayCalculator from '../DelayCalculator';
import formatDate from '../../../../../../utils/formatDate';
import { BILLABLE_OPTION } from '../../../../../Report/constants';

const boldHeader = (color) => (table) => <b style={{ color }}>{table?.column?.columnDef?.header}</b>;

// Function to get the text color based on the status
const getStatusColor = (status) => TASK_STATUS_COLORS[status?.toLowerCase()]?.text || TASK_STATUS_COLORS.default.text;

// Function to get the background color based on the status
const getBackgroundColor = (status) =>
  TASK_STATUS_COLORS[status?.toLowerCase()]?.background || TASK_STATUS_COLORS.default.background;

export const TASK_LIST_TABLE_COLUMNS = [
  {
    accessorKey: 'taskName',
    header: 'Task Name',
    size: 150,
    Header: boldHeader('#454F5B'),
    Cell: ({ row }) => <span style={{ textTransform: 'capitalize' }}>{row.original.taskName}</span>,
  },
  {
    accessorKey: 'taskType',
    header: 'Task Type',
    size: 100,
    filterVariant: 'select',
    filterSelectOptions: TASK_TYPE_LIST,
    Header: boldHeader('#454F5B'),
  },
  {
    accessorKey: 'user.userName',
    header: 'Assigned',
    size: 100,
    Header: boldHeader('#454F5B'),
    Cell: ({ row }) => <span style={{ textTransform: 'capitalize' }}>{row.original.user.userName}</span>,
  },
  {
    accessorKey: 'startDate',
    header: 'Start Date',
    enableColumnFilter: false,
    size: 150,
    Header: boldHeader('#454F5B'),
    Cell: ({ row }) => (row.original.startDate ? formatDate(row.original.startDate) : 'N/A'),
  },
  {
    accessorKey: 'endDate',
    header: 'End Date',
    enableColumnFilter: false,
    size: 150,
    Header: boldHeader('#454F5B'),
    Cell: ({ row }) => (row.original.endDate ? formatDate(row.original.endDate) : 'N/A'),
  },
  {
    accessorKey: 'daysDelayed',
    header: 'Days Delayed',
    enableSorting: false,
    size: 100,
    Header: boldHeader('#454F5B'),
    enableColumnFilter: false,
    Cell: (table) => (
      <DelayCalculator
        endDate={table?.row?.original?.endDate}
        completedAt={table?.row?.original?.completedAt}
        status={table?.row?.original?.status}
      />
    ),
  },
  {
    accessorKey: 'isBillable',
    header: 'Tasks Category',
    enableSorting: false,
    filterVariant: 'select',
    filterSelectOptions: BILLABLE_OPTION,
    Header: boldHeader('#454F5B'),
    Cell: (table) => <span>{table?.row?.original?.isBillable ? 'Billable' : 'Non-Billable'}</span>,
  },
  {
    accessorKey: 'status',
    header: 'Status',
    filterVariant: 'select',
    filterSelectOptions: TASK_STATUS_LIST,
    Header: boldHeader('#454F5B'),
    Cell: (table) => (
      <div
        style={{
          color: getStatusColor(table?.row?.original?.status),
          backgroundColor: getBackgroundColor(table?.row?.original?.status),
          fontWeight: 'bold',
          padding: '5px',
          borderRadius: '8px',
          display: 'inline-block',
        }}
      >
        {table?.row?.original?.status || 'N/A'}
      </div>
    ),
    size: 150,
  },
  {
    accessorKey: 'plannedHours',
    header: 'Estimated Hours',
    enableSorting: true,
    enableColumnFilter: true,
    Cell: (table) => <span>{table?.row?.original?.plannedHours ? table?.row?.original?.plannedHours : 'N/A'}</span>,
    size: 150,
    Header: boldHeader('#454F5B'),
  },
  {
    accessorKey: 'consumedHours',
    header: 'Consumed Hours',
    enableSorting: false,
    enableColumnFilter: false,
    Cell: (table) => (
      <span>{table?.row?.original?.consumedHours ? convertHM(table?.row?.original?.consumedHours) : 'N/A'}</span>
    ),
    size: 150,
    Header: boldHeader('#454F5B'),
  },
];

export const TASK_LIST_TABLE_DEFAULT_PROPS = {
  positionActionsColumn: 'last',
  enableFullScreenToggle: false,
  enableColumnActions: false,
  manualPagination: true,
  manualFiltering: true,
  manualSorting: true,
  muiTablePaperProps: {
    sx: {
      color: 'rgb(71 85 105 / var(--tw-text-opacity))',
    },
  },
  muiTopToolbarProps: {
    sx: {
      backgroundColor: '#fff',
    },
  },
  muiTableHeadRowProps: {
    sx: {
      '--tw-bg-opacity': 1,
      backgroundColor: 'rgb(226 232 240 / var(--tw-bg-opacity))',
      fontWeight: '900',
    },
  },
  muiTableHeadCellFilterTextFieldProps: {
    sx: {
      m: '0.5rem 0',
      borderColor: 'white',
      '& input': {
        borderColor: 'white',
        fontSize: '0.05rem',
        '&::placeholder': {
          color: 'white',
          fontSize: '0.05rem',
        },
      },
      '& .MuiInput-root:before, &.MuiInputBase-root:before': {
        borderBottomColor: 'white',
        color: 'white',
      },
      '& .MuiInputBase-input, &.MuiButtonBase-root, &.MuiIconButton-root': {
        textTransform: 'capitalize',
        color: 'white',
        fontSize: '0.05rem',
      },
    },
    '& .MuiInput-input': {
      font: 'unset !important', // Remove the inherited font
      fontSize: '12px !important', // Set the desired font size
      padding: '0.25rem 0 !important',
      color: 'white !important',
      '&::placeholder': {
        color: 'white !important',
        opacity: '0.7 !important',
        fontSize: '12px !important',
      },
    },
  },
  muiTableHeadCellProps: {
    sx: {
      backgroundColor: '#F9FAFB',
      margin: 'auto',
      // color: 'white',
      fontWeight: '800',
      fontSize: '.85rem',
      lineHeight: '2rem',
      textTransform: 'capitalize',
      '--tw-text-opacity': 1,
      // color: 'white',
      borderColor: '#F9FAFB',
      // paddingTop: '1.25rem',
      paddingRight: '1.5rem',
      paddingBottom: '1.25rem',
      paddingLeft: '1.5rem',
      ' & .Mui-TableHeadCell-Content': {
        position: 'relative',
        top: '5px',
      },
    },
  },
  muiTableBodyCellProps: {
    sx: {
      '--tw-border-opacity': 1,
      borderColor: 'rgb(241 245 249 / var(--tw-border-opacity))',
      '--tw-text-opacity': 1,
      paddingLeft: '1.5rem',
    },
  },
};

export const INITIAL_TASK_FORM_STATE = () => ({
  taskName: '',
  userId: '',
  sectionId: '',
  endDate: '',
  startDate: new Date(new Date().setHours(0, 0, 0, 0)),
  description: '',
  status: 'Not Started',
  plannedHours: undefined,
  parentTaskId: undefined,
  taskType: 'Task',
  issueType: '',
  isBillable: true,
});
